import { DatePipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import {
  CellComponent,
  CellGridComponent, AvatarComponent, BadgeComponent, ButtonComponent, EmptyStateComponent, IconComponent, LoadingSkeletonComponent, RepeatDirective, SoftSlotDirective } from '@softline/ui-core';
import { BarcodeComponent } from '@softline/ui-core/code';
import { DsTabComponent, DsTabsComponent } from '@softline/ui-core/tabs';
import {
  groupSalesPrices,
  isEndedSalesPrice,
  keyNameLabel,
  latestSalesPrices,
  sortSalesPricesHistory,
} from '../../functions/sales-prices.function';
import { flagLabel, masterdataLabel } from '../../functions/entity-label.function';
import { toStockLevelRows } from '../../functions/stock-levels.function';
import { SalesPriceResponse } from '../../types/prices';
import { ProductService } from '../../services/product.service';
import { ProductPanelItem } from './product-panel-input';

@Component({
  selector: 'soft-product-detail-panel',
  templateUrl: './product-detail-panel.component.html',
  styleUrl: './product-detail-panel.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CellGridComponent,
    CellComponent,
    DecimalPipe,
    AvatarComponent,
    BadgeComponent,
    BarcodeComponent,
    ButtonComponent,
    EmptyStateComponent,
    IconComponent,
    LoadingSkeletonComponent,
    RepeatDirective,
    SoftSlotDirective,
    DsTabsComponent,
    DsTabComponent,
  ],
  providers: [DatePipe],
})
export class ProductDetailPanelComponent {
  private readonly productService = inject(ProductService);
  private readonly datePipe = inject(DatePipe);

  readonly item = input<ProductPanelItem | null>(null);
  readonly items = input<ProductPanelItem[]>([]);
  readonly index = model<number>(0);
  /** Decimal places for prices. Pass the same value the consumer's grid uses
      (wws/sales: `decimalDigits.price`) so Einzelpreis and panel agree; this
      lib must not depend on the sales configuration itself. */
  readonly priceDecimals = input<number>(2);

  readonly closePanel = output<void>();

  protected readonly activeTab = signal(0);

  protected readonly isListed = computed(() => this.product()?.active ?? false);

  protected readonly isCustomizable = computed(
    () =>
      this.product()?.type?.key === 'customizable' ||
      this.item()?.product?.type === 'customizable'
  );

  protected readonly product = this.productService.product;
  protected readonly isLoadingProduct = this.productService.isLoading;
  /** Main product load failed (only meaningful when an id was requested). */
  protected readonly productError = computed(
    () =>
      !!this.item()?.product?.id &&
      !this.isLoadingProduct() &&
      this.productService.error() != null
  );
  protected retryLoad(): void {
    this.productService.reload();
  }
  protected readonly mainImage = this.productService.mainImage;
  protected readonly suppliers = computed(
    () => this.productService.supplierInformation() ?? []
  );
  protected readonly isLoadingSuppliers =
    this.productService.isLoadingSupplierInformation;
  protected readonly mainSupplierId = computed(
    () => this.productService.mainSupplierInformation()?.id ?? null
  );
  protected readonly unitConversions = computed(
    () => this.productService.unitConversions() ?? []
  );
  protected readonly isLoadingUnitConversions =
    this.productService.isLoadingUnitConversions;
  protected readonly salesPrices = computed(
    () => this.productService.salesPrices() ?? []
  );
  protected readonly isLoadingSalesPrices = this.productService.isLoadingSalesPrices;
  protected readonly showPriceHistory = signal(false);
  private readonly latestSalesPrices = computed(() =>
    latestSalesPrices(this.salesPrices())
  );
  protected readonly visibleSalesPrices = computed(() =>
    this.showPriceHistory()
      ? sortSalesPricesHistory(this.salesPrices())
      : this.latestSalesPrices()
  );
  protected readonly historicalPriceCount = computed(
    () => this.salesPrices().length - this.latestSalesPrices().length
  );

  /** Digit format for prices, same shape the sales grid builds from its
      configured decimal digits (`1.2-2` for two places). */
  protected readonly priceFormat = computed(() => {
    const d = Math.max(0, Math.trunc(this.priceDecimals()));
    return `1.${d}-${d}`;
  });

  /** Prices grouped by Verkaufsorganisation + Gültigkeit, one row per
      Preisstufe — the payload holds 5 Preisstufen per period. */
  protected readonly salesPriceGroups = computed(() =>
    groupSalesPrices(
      this.visibleSalesPrices(),
      (iso) => this.formatDate(iso),
      new Date()
    )
  );

  /** Organisation is usually identical for every price — then it belongs in the
      section subtitle instead of on every group. Rendered as "<Schlüssel> ·
      <Name>", the same pairing as "Status je Verkaufsorganisation". */
  protected readonly salesPriceOrg = computed(() => {
    const groups = this.salesPriceGroups();
    const labels = new Set(
      groups.map((g) => [g.orgKey, g.org].filter(Boolean).join(' · '))
    );
    return labels.size === 1 ? [...labels][0] : '';
  });

  protected readonly salesPriceUnit = computed(() => {
    const units = new Set(
      this.salesPriceGroups()
        .map((g) => g.unit)
        .filter(Boolean)
    );
    return units.size === 1 ? [...units][0] : '';
  });

  /** Inhalt/Umrechnung from `basePrice` — present in the payload but never
      shown before (e.g. 0,021 1KG je Stk = Faktor 47,619). */
  protected readonly basePriceInfo = computed(() => {
    const prices = this.salesPrices();
    const today = new Date();
    // prefer a currently valid price — an expired one may carry stale content data
    const base = (
      prices.find((sp) => sp.basePrice && !isEndedSalesPrice(sp, today)) ??
      prices.find((sp) => sp.basePrice)
    )?.basePrice;
    if (!base) return null;
    const contentUnit = keyNameLabel(base.contentUnit);
    const declarationUnit = keyNameLabel(base.declarationUnit);
    return {
      netContent: base.netContent,
      contentUnit,
      factor: base.factor,
      declarationUnit,
      consumerDeclaration: base.consumerDeclaration?.trim() || '',
    };
  });
  protected togglePriceHistory(): void {
    this.showPriceHistory.update((v) => !v);
  }
  protected isPriceEnded(sp: SalesPriceResponse): boolean {
    return isEndedSalesPrice(sp, new Date());
  }
  protected readonly salesOrganisationStatuses = computed(
    () => this.productService.salesOrganisationStatuses() ?? []
  );
  protected readonly isLoadingSalesOrganisationStatuses =
    this.productService.isLoadingSalesOrganisationStatuses;
  protected readonly stockLevels = computed(
    () => this.productService.stockLevels() ?? []
  );
  protected readonly isLoadingStockLevels = this.productService.isLoadingStockLevels;
  /** Unit the stock amounts are in — the API returns everything in the
      article's Lagereinheit. Deliberately NOT via the label helpers: their "—"
      fallback would render as a dangling dash behind the total ("3.078 —"). */
  protected readonly storageUnitName = computed(() => {
    const unit = this.product()?.storageUnit;
    return unit?.name?.trim() || unit?.key?.trim() || '';
  });
  /** Sum over the warehouses — the panel's headline number. */
  protected readonly stockLevelTotal = computed(() =>
    this.stockLevels().reduce((sum, row) => sum + (row.stockLevel ?? 0), 0)
  );
  /** Dispostand sum; null while the backend sends no disposition amounts. */
  protected readonly dispositionTotal = computed(() => {
    const values = this.stockLevels()
      .map((row) => row.dispositionQuantity)
      .filter((d): d is number => d !== null && d !== undefined);
    return values.length ? values.reduce((sum, d) => sum + d, 0) : null;
  });

  protected readonly stockLevelRows = computed(() =>
    toStockLevelRows(this.stockLevels())
  );

  /** Same key | name | value shape as the Lagerstand list. The Schlüssel is NOT
      stripped from the name here: a VKFORG key ("999-Zentrale") is not a plain
      prefix of its name, and stripping mangled valid names. */
  protected readonly salesOrganisationStatusRows = computed(() =>
    this.salesOrganisationStatuses()
      .map((s) => ({
        id: s.id,
        key: s.salesOrganisation?.key?.trim() ?? '',
        name: s.salesOrganisation?.name?.trim() ?? '',
        status: flagLabel(s.status),
      }))
      .sort((a, b) => a.key.localeCompare(b.key, 'de', { numeric: true }))
  );

  protected unitTypeLabel(
    type: { key?: string | null; name?: string | null } | null | undefined
  ): string {
    if (!type) return '';
    // handoff 451419: name carries the translated SoftTOP label; the local map
    // below only covers backends that still echo the key
    const name = type.name?.trim();
    if (name && name !== type.key?.trim()) return name;
    const normalize = (s: string | null | undefined): string =>
      s?.trim().toUpperCase().replace(/-/g, '_') ?? '';
    const token = normalize(type.key) || normalize(type.name);
    switch (token) {
      case 'PURCHASE_AND_SALES':
        return 'Einkauf & Verkauf';
      case 'PURCHASE':
        return 'Einkauf';
      case 'SALES':
        return 'Verkauf';
      case 'STORAGE':
        return 'Lager';
      case 'DISPATCH':
        return 'Versand';
      default:
        return type.name?.trim() ?? '';
    }
  }

  protected supplierInitials(supplier: { name?: string | null } | null): string {
    const name = supplier?.name?.trim();
    if (!name) return '??';
    const words = name.split(/\s+/).filter(Boolean);
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + (words[1][0] ?? '')).toUpperCase();
  }

  protected readonly title = computed(() => {
    const p = this.product();
    if (p?.description1) return p.description1;
    const fallback = this.item()?.product;
    return fallback?.description1 || this.item()?.description || '';
  });

  protected readonly descriptionLines = computed(() => {
    const p = this.product() ?? this.item()?.product;
    return [p?.description2, p?.description3, p?.description4, p?.description5].filter(
      (s): s is string => !!s?.trim()
    );
  });

  protected readonly articleNumber = computed(
    () => this.product()?.key ?? this.item()?.product?.key ?? ''
  );

  protected readonly mainGroupName = computed(() =>
    masterdataLabel(this.product()?.mainGroup)
  );
  protected readonly subGroupName = computed(() =>
    masterdataLabel(this.product()?.subGroup)
  );
  protected readonly productGroupName = computed(() =>
    masterdataLabel(this.product()?.productGroup)
  );

  protected readonly accountingSalesName = computed(() =>
    masterdataLabel(this.product()?.accountingGroupSales)
  );
  protected readonly accountingPurchaseName = computed(() =>
    masterdataLabel(this.product()?.accountingGroupPurchase)
  );
  protected readonly listedFlag = computed(() => {
    const p = this.product();
    if (!p) return '—';
    return p.active ? 'Ja' : 'Nein';
  });
  protected readonly productType = computed(() =>
    flagLabel(this.product()?.type)
  );
  protected readonly plantProtectionNumber = computed(
    () => this.product()?.plantProtectionRegisterNumber ?? '—'
  );
  protected readonly storageManaged = computed(() =>
    flagLabel(this.product()?.stockManaged)
  );

  private dash(v: string | null | undefined): string {
    return v?.trim() ? v.trim() : '—';
  }

  protected readonly subSubGroupName = computed(() =>
    masterdataLabel(this.product()?.subSubGroup)
  );
  protected readonly taxName = computed(() => masterdataLabel(this.product()?.tax));
  protected readonly termGroupName = computed(() =>
    masterdataLabel(this.product()?.termGroup)
  );
  protected readonly externalKey = computed(() => this.dash(this.product()?.externalKey));
  protected readonly externalKey2 = computed(() =>
    this.dash(this.product()?.externalKey2)
  );
  protected readonly statusName = computed(() =>
    flagLabel(this.product()?.status)
  );
  protected readonly stockStructureName = computed(() =>
    flagLabel(this.product()?.stockStructure)
  );
  protected readonly margin = computed(() => {
    const m = this.product()?.margin;
    return m == null ? '—' : `${m}`;
  });

  protected readonly unitRows = computed(() => {
    const p = this.product();
    return [
      { label: 'Verrechnungseinheit', value: masterdataLabel(p?.consumerUnit) },
      { label: 'Versandeinheit', value: masterdataLabel(p?.despatchUnit) },
      { label: 'Preiseinheit', value: masterdataLabel(p?.priceUnit) },
      { label: 'Verkaufseinheit', value: masterdataLabel(p?.salesUnit) },
      { label: 'Lagereinheit', value: masterdataLabel(p?.storageUnit) },
      { label: 'Statistikeinheit', value: masterdataLabel(p?.statisticUnit) },
    ];
  });

  protected readonly textRows = computed(() => {
    const p = this.product();
    if (!p) return [] as { label: string; value: string }[];
    const rows: [string, string | null | undefined][] = [
      ['Kurzbezeichnung', p.shortDescription],
      ['Tag 1', p.tag1],
      ['Tag 2', p.tag2],
      ['Info', p.info],
      ['Notiz', p.note],
      ['Empfängertext', p.recipientText],
      ['Zubereitung', p.preparation],
      ['Zutaten', p.ingredient],
      ['Deklaration', p.declaration],
      ['QS-Kommentar', p.qsComment],
      ['Produktion', p.production],
      ['PE-Nummer', p.peNumber],
      ['Artikeltext', p.productText],
    ];
    return rows
      .filter(([, v]) => !!v?.trim())
      .map(([label, v]) => ({ label, value: (v as string).trim() }));
  });

  protected readonly listedOn = computed(() => this.formatDate(this.product()?.listedOn));
  protected readonly discontinuedOn = computed(() =>
    this.formatDate(this.product()?.discontinuedOn)
  );
  protected readonly deletedOn = computed(() =>
    this.formatDate(this.product()?.deletedOn)
  );

  protected readonly mainImageSrc = computed(() => {
    const img = this.mainImage();
    if (!img) return null;
    if (img.externalUrl) return img.externalUrl;
    if (img.imageData) {
      const mime = img.mimeType ?? 'image/png';
      return `data:${mime};base64,${img.imageData}`;
    }
    return null;
  });

  protected readonly imageSrcs = computed<string[]>(() => {
    const list = this.productService.images() ?? [];
    return list
      .map((img) => {
        if (img.externalUrl) return img.externalUrl;
        if (img.imageData) {
          const mime = img.mimeType ?? 'image/png';
          return `data:${mime};base64,${img.imageData}`;
        }
        return null;
      })
      .filter((src): src is string => !!src);
  });

  protected readonly imageIndex = signal(0);

  protected readonly currentImageSrc = computed<string | null>(() => {
    const srcs = this.imageSrcs();
    if (srcs.length === 0) return this.mainImageSrc();
    const i = Math.min(this.imageIndex(), srcs.length - 1);
    return srcs[i] ?? null;
  });

  protected readonly imageCount = computed(() => this.imageSrcs().length);
  protected readonly canGoPrevImage = computed(() => this.imageIndex() > 0);
  protected readonly canGoNextImage = computed(
    () => this.imageIndex() < this.imageCount() - 1
  );

  protected readonly pagerLabel = computed(() => {
    const total = this.items().length;
    const i = this.index();
    if (total === 0) return '';
    return `Artikel ${i + 1} von ${total}`;
  });

  protected readonly canGoPrev = computed(() => this.index() > 0);
  protected readonly canGoNext = computed(() => this.index() < this.items().length - 1);

  constructor() {
    effect(() => {
      const id = this.item()?.product?.id ?? null;
      this.productService.productId.set(id);
      this.imageIndex.set(0);
      this.showPriceHistory.set(false);
    });
  }

  protected onPrevImage(): void {
    if (!this.canGoPrevImage()) return;
    this.imageIndex.update((i) => i - 1);
  }

  protected onNextImage(): void {
    if (!this.canGoNextImage()) return;
    this.imageIndex.update((i) => i + 1);
  }

  protected setImageIndex(i: number): void {
    this.imageIndex.set(i);
  }

  protected onPrev(): void {
    if (!this.canGoPrev()) return;
    this.index.set(this.index() - 1);
  }

  protected onNext(): void {
    if (!this.canGoNext()) return;
    this.index.set(this.index() + 1);
  }

  private formatDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    return this.datePipe.transform(iso, 'dd.MM.yyyy') ?? iso;
  }

  protected formatDateValue(iso: string | null | undefined): string {
    return this.formatDate(iso);
  }
}
